import { useEffect, useRef } from 'react';

import { cn } from '@/utils/cn';

export const CODE_LENGTH = 6;

/** Keeps the digits of `text`, at most `CODE_LENGTH` of them. */
export function digitsOf(text) {
  return String(text ?? '')
    .replace(/\D/g, '')
    .slice(0, CODE_LENGTH);
}

/**
 * Six boxes for a 6-digit code.
 *
 * Typing moves to the next box and Backspace on an empty box goes back. Pasting
 * a whole code fills every box. `onComplete` fires once all six are filled, so
 * the code is checked without a separate button.
 *
 * `value` is the code so far, as a string of digits; `onChange` receives the
 * new string.
 */
export function CodeBoxes({
  value,
  onChange,
  onComplete,
  disabled = false,
  invalid = false,
  autoFocus = false,
  label = 'Verification code',
}) {
  const refs = useRef([]);
  const digits = digitsOf(value);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function update(next) {
    const clean = digitsOf(next);
    onChange(clean);
    if (clean.length === CODE_LENGTH) onComplete?.(clean);
    return clean;
  }

  function focusBox(index) {
    refs.current[Math.max(0, Math.min(index, CODE_LENGTH - 1))]?.focus();
  }

  function handleInput(index, event) {
    let typed = digitsOf(event.target.value);
    if (!typed) return;
    // Typing into a filled box with the cursor after its digit: keep the new one.
    if (typed.length === 2 && digits[index] && typed.startsWith(digits[index])) {
      typed = typed.slice(1);
    }
    if (typed.length === 1) {
      // One digit replaces this box and leaves the others as they are.
      update(digits.slice(0, index) + typed + digits.slice(index + 1));
      focusBox(index + 1);
      return;
    }
    // A paste or an autofill can put several digits into one box.
    const clean = update(digits.slice(0, index) + typed);
    focusBox(clean.length);
  }

  function handleKeyDown(index, event) {
    if (event.key === 'Backspace') {
      event.preventDefault();
      if (digits[index]) {
        update(digits.slice(0, index) + digits.slice(index + 1));
      } else if (index > 0) {
        update(digits.slice(0, index - 1) + digits.slice(index));
        focusBox(index - 1);
      }
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusBox(index + 1);
    }
  }

  function handlePaste(event) {
    const pasted = digitsOf(event.clipboardData.getData('text'));
    if (!pasted) return;
    event.preventDefault();
    const clean = update(pasted);
    focusBox(clean.length);
  }

  return (
    <div role="group" aria-label={label} className="flex justify-between gap-2">
      {Array.from({ length: CODE_LENGTH }, (_, index) => (
        <input
          key={index}
          ref={(element) => {
            refs.current[index] = element;
          }}
          value={digits[index] ?? ''}
          onChange={(event) => handleInput(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
          onFocus={(event) => event.target.select()}
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          maxLength={CODE_LENGTH}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${CODE_LENGTH}`}
          aria-invalid={invalid || undefined}
          className={cn(
            'border-line bg-surface text-ink rounded-input h-12 w-full min-w-0 border text-center text-xl font-semibold tabular-nums shadow-xs transition',
            'hover:border-brand-300 focus:border-brand-500 focus:ring-brand-100 focus:ring-2 focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-60',
            invalid && 'border-danger-500 focus:border-danger-500 focus:ring-danger-100',
          )}
        />
      ))}
    </div>
  );
}
